import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { CardSelect } from '../CardSelect/CardSelect';
import { PromptStandardIcon } from '../Icons/PromptStandardIcon';
import { colors } from '../Spinner/utils/colors';
import { TerritoryNewPostIcon } from '../Icons/TerritoryNewPostIcon';
import ArrowLeftIcon from '../Icons/ArrowLeftIcon';
import { CreatePromptForm } from '../CreatePrompt/CreatePromptForm';
import { PromptSettingsLiveStatus, PromptTypePicker } from '../Prompt/constants';
import { TPromptFormFields } from '../PromptForm/types';
import { hasPermissionToCreateTerritoryPostsAndPrompts } from './hooks/hasPermissionToCreateTerritoryPostsAndPrompts';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';

type WriteInTerritoryPickerProps = {
  promptType: PromptTypePicker;
  territoryId: string;
  territorySlug: string;
  url?: string;
  useFormReturn: UseFormReturn<TPromptFormFields>;
  isPromptCardSelected?: boolean;
  onPromptSelect: () => void;
  onBackClick: () => void;
  closeModal: () => void;
  setPromptType?: (type?: PromptTypePicker) => void;
};

export const WriteInTerritoryPicker: React.FC<WriteInTerritoryPickerProps> = ({
  territoryId,
  territorySlug,
  url,
  isPromptCardSelected = false,
  useFormReturn,
  promptType,
  onPromptSelect,
  onBackClick,
  closeModal,
  setPromptType,
}) => {
  const { hasCreatePostPermission, hasCreatePromptPermission } =
    hasPermissionToCreateTerritoryPostsAndPrompts(territoryId);
  const { sendEvent } = useAnalytics();

  const renderBackButton = () => (
    <button
      onClick={onBackClick}
      className="absolute left-6 top-6 flex gap-2 items-center text-t2-neutral-grey08 hover:text-t2-primary-brandColor"
    >
      <ArrowLeftIcon className="stroke-current" /> Back
    </button>
  );

  const renderPromptForm = () => (
    <CreatePromptForm
      promptType={promptType}
      promptSettingsLiveStatus={promptType === PromptTypePicker.LIVE ? PromptSettingsLiveStatus.UPCOMING : undefined}
      territoryId={territoryId}
      closeModal={closeModal}
      useFormReturn={useFormReturn}
      setPromptType={setPromptType}
    />
  );

  const renderCardOptions = () => (
    <div className="mt-3 flex flex-col sm:flex-row flex-shrink-0 gap-8">
      <div className="basis-full md:basis-1/2">
        <CardSelect
          title="Post"
          description="Best for longer content to share your thoughts or stories"
          icon={
            <TerritoryNewPostIcon
              stroke={hasCreatePostPermission ? '#D97706' : colors?.t2.neutral.grey05}
              fill={hasCreatePostPermission ? '#FFFBEB' : colors?.t2.neutral.grey03}
            />
          }
          as={hasCreatePostPermission ? 'a' : 'div'}
          href={url}
          disabled={!hasCreatePostPermission}
          territorySlug={territorySlug}
          onClick={() => sendEvent('territory_create_post_button_click')}
        />
      </div>
      <div className="basis-full md:basis-1/2">
        <CardSelect
          title="Prompt"
          description="Best for conversation starters or to spark new discussions"
          icon={
            <PromptStandardIcon
              stroke={hasCreatePromptPermission ? colors?.t2.success.darkGreen : colors?.t2.neutral.grey05}
              fill={hasCreatePromptPermission ? colors?.t2.success.lightGreen : colors?.t2.neutral.grey03}
            />
          }
          onClick={onPromptSelect}
          disabled={!hasCreatePromptPermission}
          territorySlug={territorySlug}
        />
      </div>
    </div>
  );

  return (
    <div>
      {isPromptCardSelected ? (
        <>
          {renderBackButton()}
          {renderPromptForm()}
        </>
      ) : (
        renderCardOptions()
      )}
    </div>
  );
};
